import { useState, useMemo } from 'react';
import { Eye, Code2, RotateCcw, ChevronDown, ChevronRight } from 'lucide-react';
import { WhatsAppMessagePreview } from '@/components/common/WhatsAppMessagePreview';
import { clsx } from 'clsx';

export type TemplateVariableCategory = 'cliente' | 'cita' | 'precios' | 'historial' | 'general';

export interface TemplateVariableOption {
  token: string;
  label: string;
  category?: TemplateVariableCategory;
}

export interface TemplateEditorProps {
  title: string;
  template: string;
  onChange: (template: string) => void;
  variables: TemplateVariableOption[];
  rows?: number;
  defaultTemplate?: string;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  previewType?: 'appointment' | 'crm' | 'campaign';
  icon?: React.ReactNode;
}

const CATEGORY_LABELS: Record<TemplateVariableCategory, { label: string; color: string }> = {
  cliente: { label: 'Cliente', color: 'text-blue-500 bg-blue-500/10 border-blue-500/20' },
  cita: { label: 'Cita / Profesional', color: 'text-purple-500 bg-purple-500/10 border-purple-500/20' },
  precios: { label: 'Precios', color: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20' },
  historial: { label: 'Historial', color: 'text-amber-500 bg-amber-500/10 border-amber-500/20' },
  general: { label: 'General', color: 'text-indigo-500 bg-indigo-500/10 border-indigo-500/20' },
};

export function TemplateEditor({
  title,
  template,
  onChange,
  variables,
  rows = 3,
  defaultTemplate,
  collapsible = false,
  defaultExpanded = true,
  previewType = 'appointment',
  icon,
}: TemplateEditorProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [showPreview, setShowPreview] = useState(false);
  const [activeCategoryFilter, setActiveCategoryFilter] = useState<string | null>(null);
  const insertVariable = (token: string) => {
    onChange(`${template ? template.trim() : ''} {${token}}`.trim());
  };

  const isCustomized = defaultTemplate !== undefined && template !== defaultTemplate;
  const charCount = (template || '').length;

  const categories = useMemo(() => {
    const cats = new Set<TemplateVariableCategory>();
    variables.forEach((v) => {
      if (v.category) cats.add(v.category);
    });
    return Array.from(cats);
  }, [variables]);

  const filteredVariables = useMemo(() => {
    if (!activeCategoryFilter) return variables;
    return variables.filter((v) => v.category === activeCategoryFilter);
  }, [variables, activeCategoryFilter]);

  return (
    <div className="bg-surface border border-border rounded-xl transition-all shadow-xs overflow-hidden">
      {/* Header */}
      <div
        className={clsx(
          'p-3.5 flex items-center justify-between gap-3',
          collapsible && 'cursor-pointer select-none hover:bg-surface-secondary/40 transition-colors'
        )}
        onClick={() => collapsible && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          {collapsible && (
            <div className="text-text-muted">
              {isExpanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            </div>
          )}
          {icon && <div className="text-brand-primary shrink-0">{icon}</div>}
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-xs font-semibold text-text-primary truncate">{title}</h4>
            {isCustomized && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-brand-primary/10 text-brand-primary font-bold">
                Personalizado
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
          {defaultTemplate !== undefined && (
            <button
              type="button"
              onClick={() => {
                onChange(defaultTemplate);
                if (collapsible && !isExpanded) {
                  setIsExpanded(true);
                }
              }}
              disabled={template === defaultTemplate}
              className={clsx(
                'flex items-center gap-1 text-[11px] text-text-muted hover:text-text-primary transition-colors cursor-pointer px-2 py-1 rounded-md hover:bg-surface-secondary',
                template === defaultTemplate && 'opacity-30 cursor-not-allowed hover:bg-transparent hover:text-text-muted'
              )}
              title="Restablecer a plantilla predeterminada"
            >
              <RotateCcw size={12} />
              <span className="hidden sm:inline">Restablecer</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => {
              setShowPreview(!showPreview);
              if (collapsible && !isExpanded) {
                setIsExpanded(true);
              }
            }}
            className={clsx(
              'flex items-center gap-1 text-[11px] px-2 py-1 rounded-md font-medium transition-colors cursor-pointer',
              showPreview
                ? 'bg-brand-primary text-white'
                : 'bg-surface-secondary text-brand-primary hover:bg-surface-tertiary'
            )}
          >
            {showPreview ? <Code2 size={12} /> : <Eye size={12} />}
            <span>{showPreview ? 'Editor' : 'Vista WA'}</span>
          </button>
        </div>
      </div>

      {/* Body Content */}
      {(!collapsible || isExpanded) && (
        <div className="p-3.5 pt-0 space-y-3 border-t border-border-light/60 animate-fade-in">
          {showPreview ? (
            <div className="pt-2">
              <WhatsAppMessagePreview
                template={template}
                previewType={previewType}
                variant="simulator"
                showCharCount
                footerNote="Variables sustituidas con datos de ejemplo"
              />
            </div>
          ) : (
            /* Textarea Editor */
            <div className="space-y-2.5 pt-2">
              <textarea
                value={template}
                onChange={(e) => onChange(e.target.value)}
                rows={rows}
                placeholder="Escribe el texto de la plantilla..."
                className="w-full p-3 bg-surface-secondary border border-border rounded-xl text-xs text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 font-sans leading-relaxed resize-y"
              />

              {/* Categorized Variables Header Filter (if categories exist) */}
              {categories.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                  <span className="text-[10px] text-text-muted font-semibold mr-1">Filtrar tags:</span>
                  <button
                    type="button"
                    onClick={() => setActiveCategoryFilter(null)}
                    className={clsx(
                      'px-2 py-0.5 rounded text-[10px] font-medium transition-colors cursor-pointer',
                      activeCategoryFilter === null
                        ? 'bg-brand-primary text-white'
                        : 'bg-surface-secondary text-text-muted hover:text-text-primary'
                    )}
                  >
                    Todos ({variables.length})
                  </button>
                  {categories.map((cat) => {
                    const info = CATEGORY_LABELS[cat] || { label: cat, color: 'text-text-secondary bg-surface-secondary' };
                    const isCatActive = activeCategoryFilter === cat;
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setActiveCategoryFilter(isCatActive ? null : cat)}
                        className={clsx(
                          'px-2 py-0.5 rounded text-[10px] font-medium transition-colors cursor-pointer border',
                          isCatActive
                            ? 'bg-brand-primary text-white border-brand-primary'
                            : `${info.color} hover:opacity-80`
                        )}
                      >
                        {info.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Variable Insertion Chips */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-1 border-t border-border-light/60">
                <div className="flex flex-wrap gap-1.5 items-center">
                  <span className="text-[10px] text-text-muted font-medium mr-0.5">Insertar:</span>
                  {filteredVariables.map((v) => (
                    <button
                      key={v.token}
                      type="button"
                      onClick={() => insertVariable(v.token)}
                      className="px-2 py-0.5 bg-surface-secondary hover:bg-surface-tertiary border border-border text-[10px] rounded-md font-mono text-text-secondary hover:text-brand-primary transition-colors cursor-pointer"
                      title={v.label}
                    >
                      {`{${v.token}}`}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2 self-end sm:self-center text-[11px] text-text-muted shrink-0">
                  <span className={clsx(charCount > 500 && 'text-amber-500 font-medium')}>
                    {charCount} caracteres
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
